import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import AuthService from "../../../services/AuthService";
import { sessionSuccess } from "../../../store/slices/authSlice";
import { resetAll } from "../../../store/slices/baseSlice";
import { clearSignedOut, redirectTo, safeTarget } from "../../../helpers/SsoHelper";
import * as PendingRedirect from "../../../helpers/PendingRedirect";
import { errorText, TOO_MANY_ATTEMPTS } from "../../../helpers/ErrorMessage";
import { APP_ROUTE_LOGIN } from "../../../constants/RouteConstants";
import PreLoginContainer from "../../common/preLoginContainer";
import Link from "../../common/link";

// The code of this tab's last company sign-in, so a reload of this page does
// not send a used code again.
const DONE_KEY = "edexlms.ssoHandoffDone";

const readDone = () => {
  try {
    return window.sessionStorage.getItem(DONE_KEY);
  } catch (error) {
    return null;
  }
};

const writeDone = (code) => {
  try {
    window.sessionStorage.setItem(DONE_KEY, code);
  } catch (error) {
    // Storage can be unavailable (private mode); a reload then shows the expired message.
  }
};

// /sso/complete: the company vouched for the person; the one-time code from
// the API becomes the session, as a password sign-in's does.
export const SsoComplete = ({ sessionSuccess, resetAll, signedIn }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [failed, setFailed] = useState(false);
  const started = useRef(false);
  const code = searchParams.get("code");
  const target = safeTarget(searchParams.get("target"));

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    if (!code) {
      setFailed(true);
      return;
    }

    if (readDone() === code) {
      navigate(target || "/", { replace: true });
      return;
    }

    AuthService.ssoHandoff({ code })
      .then(async (ajax) => {
        writeDone(code);
        if (signedIn) {
          // Someone was already signed in in this browser. Their session ends
          // on the server (best effort: it may have expired already), and the
          // whole saved store is cleared, as signing out does, so none of
          // their data or choices (such as the admin dashboard) carry over.
          await AuthService.logout().catch(() => null);
          resetAll();
        }
        clearSignedOut();
        PendingRedirect.rememberSignInTarget(target);
        sessionSuccess(ajax.data);
        if (signedIn) {
          // A fresh load of the app, for the new person only.
          redirectTo(target || "/");
        } else {
          navigate(target || "/", { replace: true });
        }
      })
      .catch((reason) => {
        // Over the rate limit (F21) the answer says how long to wait.
        const body = reason?.response?.data;
        setFailed(body?.response === TOO_MANY_ATTEMPTS ? body : true);
      });
  }, [code, target, navigate, sessionSuccess, resetAll, signedIn]);

  return (
    <PreLoginContainer>
      {(failed && (
        <div className="flex flex-column gap-3 align-items-center mt-4">
          <div className="error-message">
            {failed?.response === TOO_MANY_ATTEMPTS
              ? errorText(failed, intl)
              : intl.formatMessage({ id: "sso_code_invalid" })}
          </div>
          <Link to={APP_ROUTE_LOGIN}>
            {intl.formatMessage({ id: "back_to_login" })}
          </Link>
        </div>
      )) || (
        <div className="flex flex-column gap-3 align-items-center mt-4">
          <ProgressSpinner style={{ width: "40px", height: "40px" }} />
          <div>{intl.formatMessage({ id: "sso_completing" })}</div>
        </div>
      )}
    </PreLoginContainer>
  );
};

const mapStateToProps = (state) => ({
  signedIn: Boolean(state.auth?.session?.refreshToken),
});

const mapDispatchToProps = (dispatch) => ({
  sessionSuccess: (data) => dispatch(sessionSuccess(data)),
  resetAll: () => dispatch(resetAll()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SsoComplete);
